import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  X,
  Crown,
  Zap,
  Sparkles,
  ShoppingCart,
  Bitcoin,
  Loader2,
  ArrowRight,
  Copy,
  ExternalLink,
  CheckCircle,
  Clock,
  MessageCircle,
  Store,
  Send,
  Package,
  Tags,
  Download,
  HardDrive,
  AlertCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import QRCode from 'qrcode';

interface CryptoWallet {
  id: string;
  name: string;
  symbol: string;
  network: string | null;
  address: string;
  color: string | null;
}

interface Plan {
  id: string;
  name: string;
  price: number;
  yearlyPrice: number;
  currency: string;
  features: string[];
  limits: {
    stores: number;
    products: number;
    labels: number;
    orders: number;
    downloads: number;
    storage: string;
  };
  popular?: boolean;
}

interface Subscription {
  plan: string;
  status: string;
  currentPeriodEnd: string;
  storesUsed: number;
  storesLimit: number;
  productsUsed: number;
  productsLimit: number;
}

// Static plan definitions
const PLANS: Plan[] = [
  {
    id: 'FREE',
    name: 'Free',
    price: 0,
    yearlyPrice: 0,
    currency: 'EUR',
    features: [
      '1 store',
      '15 products',
      '10 labels',
      'Basic templates',
      '5 downloads/month',
      '100 MB storage',
    ],
    limits: {
      stores: 1,
      products: 15,
      labels: 10,
      orders: 20,
      downloads: 5,
      storage: '100 MB',
    },
  },
  {
    id: 'PRO',
    name: 'Pro',
    price: 49,
    yearlyPrice: 470,
    currency: 'EUR',
    features: [
      '3 stores',
      '200 products',
      '100 labels',
      'All templates',
      'CSV import/export',
      'Bulk operations',
      'Email & Telegram notifications',
      'Priority support',
      '5 GB storage',
    ],
    limits: {
      stores: 3,
      products: 200,
      labels: 100,
      orders: 500,
      downloads: 50,
      storage: '5 GB',
    },
    popular: true,
  },
  {
    id: 'ENTERPRISE',
    name: 'Enterprise',
    price: 299,
    yearlyPrice: 2870,
    currency: 'EUR',
    features: [
      'Unlimited stores',
      'Unlimited products',
      'Unlimited labels',
      'Custom domain',
      'API access',
      'White-label (remove branding)',
      'Dedicated account manager',
      'Phone support',
      '50 GB storage',
    ],
    limits: {
      stores: -1,
      products: -1,
      labels: -1,
      orders: -1,
      downloads: -1,
      storage: '50 GB',
    },
  },
];

type BillingCycle = 'monthly' | 'yearly';

export default function PricingPage() {
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [selectedWallet, setSelectedWallet] = useState<CryptoWallet | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [userNote, setUserNote] = useState('');
  const [step, setStep] = useState<'plans' | 'payment' | 'pending' | 'success'>('plans');

  // Fetch current subscription
  const { data: subscriptionData } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => api.get<{ subscription: Subscription }>('/subscriptions/me'),
  });

  // Fetch crypto wallets
  const { data: walletsData } = useQuery({
    queryKey: ['payment-wallets'],
    queryFn: () => api.get<{ wallets: CryptoWallet[] }>('/subscriptions/payment-wallets'),
  });

  const wallets = walletsData?.wallets || [];
  const currentPlan = subscriptionData?.subscription?.plan || 'FREE';

  // Create payment mutation
  const createPaymentMutation = useMutation({
    mutationFn: (data: {
      planUpgrade: string;
      periodMonths: number;
      amount: number;
      cryptoCurrency: string;
      cryptoNetwork: string | null;
      walletAddress: string;
      userNote?: string;
    }) => api.post('/subscriptions/create-payment', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      setStep('pending');
    },
    onError: () => {
      toast.error('Failed to create payment request');
    },
  });

  // Auto-select first wallet
  useEffect(() => {
    if (wallets.length > 0 && !selectedWallet) {
      setSelectedWallet(wallets[0]);
    }
  }, [wallets, selectedWallet]);

  // Generate QR code for crypto payment
  useEffect(() => {
    if (selectedPlan && selectedWallet) {
      const walletAddress = selectedWallet.address;

      // Generate payment URI
      let paymentUri = walletAddress;
      if (selectedWallet.symbol === 'BTC') {
        paymentUri = `bitcoin:${walletAddress}`;
      } else if (selectedWallet.symbol === 'ETH') {
        paymentUri = `ethereum:${walletAddress}`;
      }

      QRCode.toDataURL(paymentUri, {
        width: 200,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' },
      }).then(setQrDataUrl);
    }
  }, [selectedPlan, selectedWallet, billingCycle]);

  const handleSelectPlan = (plan: Plan) => {
    if (plan.id === currentPlan || plan.id === 'FREE') return;
    setSelectedPlan(plan);
    setStep('payment');
  };

  const handleCopyAddress = () => {
    if (selectedWallet) {
      navigator.clipboard.writeText(selectedWallet.address);
      toast.success('Address copied!');
    }
  };

  const handlePaymentSent = () => {
    if (!selectedPlan || !selectedWallet) return;

    const price = billingCycle === 'yearly' ? selectedPlan.yearlyPrice : selectedPlan.price;

    createPaymentMutation.mutate({
      planUpgrade: selectedPlan.id,
      periodMonths: billingCycle === 'yearly' ? 12 : 1,
      amount: price,
      cryptoCurrency: selectedWallet.symbol,
      cryptoNetwork: selectedWallet.network,
      walletAddress: selectedWallet.address,
      userNote: userNote || undefined,
    });
  };

  const resetFlow = () => {
    setSelectedPlan(null);
    setUserNote('');
    setStep('plans');
  };

  const getPrice = (plan: Plan) => {
    if (plan.price === 0) return 'Free';
    return billingCycle === 'yearly'
      ? `€${Math.round(plan.yearlyPrice / 12)}`
      : `€${plan.price}`;
  };

  const getSavings = (plan: Plan) => {
    if (plan.price === 0) return null;
    const monthlyTotal = plan.price * 12;
    const savings = monthlyTotal - plan.yearlyPrice;
    return savings > 0 ? `Save €${savings}` : null;
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-6">
            Unlock the full power of ViaTecnologia.
            Create more stores and grow your business.
          </p>

          {/* Billing Toggle */}
          {step === 'plans' && (
            <div className="inline-flex items-center gap-3 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={clsx(
                  'px-4 py-2 rounded-md text-sm font-medium transition-all',
                  billingCycle === 'monthly'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                )}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={clsx(
                  'px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2',
                  billingCycle === 'yearly'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                )}
              >
                Yearly
                <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                  -20%
                </span>
              </button>
            </div>
          )}
        </motion.div>
      </div>

      <AnimatePresence mode="wait">
        {/* Step 1: Plans */}
        {step === 'plans' && (
          <motion.div
            key="plans"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid md:grid-cols-3 gap-6"
          >
            {PLANS.map((plan, index) => {
              const isCurrentPlan = plan.id === currentPlan;
              const isPro = plan.id === 'PRO';
              const isEnterprise = plan.id === 'ENTERPRISE';
              const savings = getSavings(plan);

              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={clsx(
                    'relative rounded-2xl p-6 border-2 transition-all',
                    isPro
                      ? 'border-[#4A7C2E] bg-green-50/50 scale-105 shadow-xl shadow-[#4A7C2E]/10'
                      : 'border-gray-200 bg-white hover:border-gray-300',
                    isCurrentPlan && 'ring-2 ring-[#4A7C2E] ring-offset-2 ring-offset-[#F5F1E8]'
                  )}
                >
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-500 text-white">
                        <Sparkles size={14} />
                        MOST POPULAR
                      </span>
                    </div>
                  )}

                  {/* Current Plan Badge */}
                  {isCurrentPlan && (
                    <div className="absolute -top-4 right-4">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-bold bg-sky-50 text-sky-700 border border-sky-200">
                        CURRENT
                      </span>
                    </div>
                  )}

                  {/* Plan Icon */}
                  <div className={clsx(
                    'w-14 h-14 rounded-xl flex items-center justify-center mb-4 border-2',
                    isEnterprise ? 'bg-amber-50 border-amber-200' : isPro ? 'bg-green-50 border-[#4A7C2E]/30' : 'bg-gray-100 border-gray-300'
                  )}>
                    {isEnterprise ? (
                      <Crown className="text-[#C9A961]" size={24} />
                    ) : isPro ? (
                      <Zap className="text-[#4A7C2E]" size={24} />
                    ) : (
                      <Store className="text-slate-600" size={24} />
                    )}
                  </div>

                  {/* Plan Name & Price */}
                  <h3 className="text-2xl font-bold text-slate-900 mb-1">{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-4xl font-bold text-slate-900">
                      {getPrice(plan)}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-slate-600">/month</span>
                    )}
                  </div>
                  {billingCycle === 'yearly' && savings && (
                    <p className="text-sm text-green-600 font-medium mb-4">{savings}</p>
                  )}
                  {billingCycle === 'yearly' && plan.price > 0 && (
                    <p className="text-xs text-slate-500 mb-4">
                      Billed €{plan.yearlyPrice}/year
                    </p>
                  )}

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="text-[#4A7C2E] shrink-0 mt-0.5" size={16} />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={isCurrentPlan || plan.id === 'FREE'}
                    className={clsx(
                      'w-full py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2',
                      isCurrentPlan || plan.id === 'FREE'
                        ? 'bg-gray-200 text-slate-500 cursor-not-allowed'
                        : isPro
                          ? 'bg-[#4A7C2E] text-white hover:bg-[#3A5F1E]'
                          : 'bg-gray-100 text-slate-900 hover:bg-gray-200 border-2 border-gray-300'
                    )}
                  >
                    {isCurrentPlan ? (
                      'Current Plan'
                    ) : plan.price === 0 ? (
                      'Free Forever'
                    ) : (
                      <>
                        Upgrade Now
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Step 2: Crypto Payment */}
        {step === 'payment' && selectedPlan && (
          <motion.div
            key="payment"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-2xl mx-auto"
          >
            <div className="card bg-white border border-gray-200 shadow-lg">
              <button
                onClick={resetFlow}
                className="text-slate-600 hover:text-slate-900 mb-4 flex items-center gap-1"
              >
                ← Back to plans
              </button>

              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  Upgrade to {selectedPlan.name}
                </h2>
                <p className="text-slate-600">
                  €{billingCycle === 'yearly' ? selectedPlan.yearlyPrice : selectedPlan.price}
                  {billingCycle === 'yearly' ? '/year' : '/month'}
                </p>
              </div>

              {/* Crypto Selection */}
              <div className="mb-6">
                <label className="label mb-2">Select Cryptocurrency</label>
                {wallets.length === 0 ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
                    <AlertCircle className="mx-auto mb-2 text-amber-500" size={24} />
                    <p className="text-amber-700 text-sm">
                      No payment wallets configured. Please contact support.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {wallets.map((wallet) => (
                      <button
                        key={wallet.id}
                        onClick={() => setSelectedWallet(wallet)}
                        className={clsx(
                          'p-3 rounded-lg text-center font-medium transition-all border-2',
                          selectedWallet?.id === wallet.id
                            ? 'border-[#4A7C2E] bg-green-50'
                            : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                        )}
                      >
                        <div className="font-bold text-slate-900">{wallet.symbol}</div>
                        {wallet.network && (
                          <div className="text-xs text-slate-500">{wallet.network}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment Details */}
              {selectedWallet && (
                <div className="p-6 bg-gray-50 border border-gray-200 rounded-xl mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-slate-600">Amount to pay:</span>
                    <span className="text-2xl font-bold text-slate-900">
                      €{billingCycle === 'yearly' ? selectedPlan.yearlyPrice : selectedPlan.price}
                    </span>
                  </div>

                  {/* QR Code */}
                  {qrDataUrl && (
                    <div className="flex justify-center mb-4">
                      <div className="p-4 bg-white rounded-xl border border-gray-200">
                        <img src={qrDataUrl} alt="Payment QR" className="w-48 h-48" />
                      </div>
                    </div>
                  )}

                  {/* Wallet Address */}
                  <div className="space-y-2">
                    <label className="label flex items-center gap-2">
                      <Bitcoin size={16} className="text-amber-500" />
                      Send {selectedWallet.symbol} to:
                      {selectedWallet.network && (
                        <span className="text-amber-600 text-xs">({selectedWallet.network})</span>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={selectedWallet.address}
                        className="input flex-1 font-mono text-sm bg-white"
                      />
                      <button
                        onClick={handleCopyAddress}
                        className="btn-secondary p-3"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* User Note */}
              <div className="mb-6">
                <label className="label">Note for admin (optional)</label>
                <input
                  type="text"
                  value={userNote}
                  onChange={(e) => setUserNote(e.target.value)}
                  placeholder="e.g., Transaction ID, your name..."
                  className="input"
                />
              </div>

              {/* Confirmation */}
              <button
                onClick={handlePaymentSent}
                className="w-full btn-primary py-4 text-lg"
                disabled={!selectedWallet || createPaymentMutation.isPending}
              >
                {createPaymentMutation.isPending ? (
                  <Loader2 size={24} className="animate-spin" />
                ) : (
                  <>
                    <CheckCircle size={24} />
                    I've Sent the Payment
                  </>
                )}
              </button>

              <p className="text-center text-sm text-slate-500 mt-4">
                After sending payment, our team will verify it and activate your subscription within 24 hours.
              </p>

              {/* Contact Option */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-slate-600 text-center mb-4">
                  Need help? Contact us directly:
                </p>
                <div className="flex gap-4 justify-center">
                  <a
                    href="https://wa.me/66xxxxxxxxx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-green-600 hover:text-green-700"
                  >
                    <MessageCircle size={16} />
                    WhatsApp
                    <ExternalLink size={12} />
                  </a>
                  <a
                    href="https://t.me/viatecnologia"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sky-600 hover:text-sky-700"
                  >
                    <Send size={16} />
                    Telegram
                    <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Pending Verification */}
        {step === 'pending' && (
          <motion.div
            key="pending"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md mx-auto text-center"
          >
            <div className="card bg-white border border-gray-200 shadow-lg py-12">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="text-amber-500" size={40} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Payment Submitted!
              </h2>
              <p className="text-slate-600 mb-6">
                Thank you! Our team will verify your payment and activate your subscription within 24 hours.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                You'll receive an email notification once your subscription is active.
              </p>
              <button
                onClick={() => window.location.href = '/shop'}
                className="btn-primary"
              >
                <ShoppingCart size={16} />
                Continue to Dashboard
              </button>
            </div>
          </motion.div>
        )}

        {/* Step 4: Success */}
        {step === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md mx-auto text-center"
          >
            <div className="card bg-white border border-gray-200 shadow-lg py-12">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.2 }}
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center"
              >
                <CheckCircle className="text-green-500" size={40} />
              </motion.div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Welcome to {selectedPlan?.name}! 🎉
              </h2>
              <p className="text-slate-600 mb-6">
                Your subscription has been activated. Start creating your stores!
              </p>
              <button
                onClick={() => window.location.href = '/shop'}
                className="btn-primary"
              >
                <ShoppingCart size={16} />
                Go to ShopFather
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Features Comparison */}
      {step === 'plans' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-16"
        >
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Compare Features
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-300">
                  <th className="text-left p-4 text-slate-700 font-semibold">Feature</th>
                  <th className="text-center p-4 text-slate-700 font-semibold">Free</th>
                  <th className="text-center p-4 text-green-600 font-semibold">Pro</th>
                  <th className="text-center p-4 text-amber-600 font-semibold">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {([
                  { feature: 'Stores', free: '1', pro: '3', enterprise: 'Unlimited', icon: Store },
                  { feature: 'Products', free: '15', pro: '200', enterprise: 'Unlimited', icon: Package },
                  { feature: 'Labels', free: '10', pro: '100', enterprise: 'Unlimited', icon: Tags },
                  { feature: 'Orders/month', free: '20', pro: '500', enterprise: 'Unlimited', icon: ShoppingCart },
                  { feature: 'Downloads/month', free: '5', pro: '50', enterprise: 'Unlimited', icon: Download },
                  { feature: 'Storage', free: '100 MB', pro: '5 GB', enterprise: '50 GB', icon: HardDrive },
                  { feature: 'All Templates', free: false, pro: true, enterprise: true },
                  { feature: 'All Label Categories', free: false, pro: true, enterprise: true },
                  { feature: 'CSV Import/Export', free: false, pro: true, enterprise: true },
                  { feature: 'Bulk Operations', free: false, pro: true, enterprise: true },
                  { feature: 'Remove Watermark', free: false, pro: true, enterprise: true },
                  { feature: 'Remove Branding', free: false, pro: true, enterprise: true },
                  { feature: 'Email Notifications', free: false, pro: true, enterprise: true },
                  { feature: 'Telegram Notifications', free: false, pro: true, enterprise: true },
                  { feature: 'Custom Domain', free: false, pro: false, enterprise: true },
                  { feature: 'API Access', free: false, pro: false, enterprise: true },
                  { feature: 'White-label', free: false, pro: false, enterprise: true },
                  { feature: 'Priority Support', free: false, pro: true, enterprise: true },
                  { feature: 'Phone Support', free: false, pro: false, enterprise: true },
                ] as const).map((row, i) => {
                  const Icon = 'icon' in row ? row.icon : null;
                  return (
                    <tr key={i} className="border-b border-gray-200">
                      <td className="p-4 text-slate-900 font-medium flex items-center gap-2">
                        {Icon && <Icon size={16} className="text-slate-400" />}
                        {row.feature}
                      </td>
                      {(['free', 'pro', 'enterprise'] as const).map((plan) => {
                        const value = row[plan];
                        return (
                          <td key={plan} className="text-center p-4">
                            {typeof value === 'boolean' ? (
                              value ? (
                                <Check className="mx-auto text-green-600" size={16} />
                              ) : (
                                <X className="mx-auto text-slate-300" size={16} />
                              )
                            ) : (
                              <span className={clsx(
                                'font-medium',
                                plan === 'pro' ? 'text-green-600' :
                                  plan === 'enterprise' ? 'text-amber-600' : 'text-slate-700'
                              )}>
                                {value}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* FAQ */}
      {step === 'plans' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16"
        >
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: 'What payment methods do you accept?',
                a: 'We accept Bitcoin (BTC), Ethereum (ETH), USDT, USDC, Litecoin (LTC), and Monero (XMR). Payments are verified manually within 24 hours.',
              },
              {
                q: 'How long until my subscription is activated?',
                a: 'After sending your crypto payment, our team verifies it within 24 hours. You\'ll receive an email notification once activated.',
              },
              {
                q: 'Can I cancel anytime?',
                a: 'Yes! You can cancel your subscription at any time. Your access will continue until the end of the billing period.',
              },
              {
                q: 'Do you offer refunds?',
                a: 'We offer a 7-day money-back guarantee if you\'re not satisfied with the service. Contact us for refund requests.',
              },
            ].map((faq, i) => (
              <div key={i} className="p-6 bg-white rounded-xl border border-gray-200">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Crypto Payment Note */}
      {step === 'plans' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg">
            <Bitcoin className="text-amber-500" size={20} />
            <span className="text-amber-700 text-sm">
              All payments are processed in cryptocurrency
            </span>
          </div>
        </motion.div>
      )}
    </div>
  );
}
